/* FOXSUR motor game. Subscription is handled exclusively by Shopify Forms. */
(() => {
  'use strict';
  if (customElements.get('fox-challenge')) return;
  const KEY = 'fox-challenge:v1';
  const DAY = 86400000;
  const due = (state, now, delay, cooldown) => !state.submitted && (!state.closedAt || now - state.closedAt >= cooldown * DAY) && now - state.firstSeen >= delay * 1000;
  class FoxChallenge extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.engine = this.querySelector('[data-fc-engine]');
      this.needle = this.querySelector('[data-fc-needle]');
      this.rpmDisplay = this.querySelector('[data-fc-rpm]');
      this.rpmFormat = new Intl.NumberFormat('ro-RO');
      this.gameStatus = this.querySelector('[data-fc-game-status]');
      this.liveStatus = this.querySelector('[data-fc-live]');
      this.formScreen = this.querySelector('[data-fc-screen="form"]');
      this.rpm = 0;
      this.running = false;
      this.won = false;
      this.period = Math.min(2, Math.max(0.5, Number(this.dataset.sweep) || 1.1)) * 1000;
      this.delay = Math.max(5, Number(this.dataset.delay) || 30);
      this.cooldown = Math.max(1, Number(this.dataset.cooldown) || 7);
      this.state = { firstSeen: Date.now() };
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.querySelectorAll('[data-fc-close]').forEach(button => button.addEventListener('click', () => this.close(true), options));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(true); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close(true);
      }, options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && this.dialog.open) this.close(true); }, options);
      this.engine.addEventListener('click', () => this.toggleEngine(), options);
      window.addEventListener('blur', () => this.pause(), options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(); }, options);
      this.querySelector('[data-fc-preview]')?.addEventListener('click', () => this.open(true), options);
      document.addEventListener('visitorConsentCollected', () => this.privacyChanged(), options);
      this.paint(0);
      this.privacyChanged();
      if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error && this.isConnected) this.privacyChanged(); });
      }
    }
    disconnectedCallback() {
      this.pause(); clearInterval(this.timer); clearInterval(this.formsTimer); clearTimeout(this.transitionTimer);
      this.abort?.abort(); this.formsObserver?.disconnect();
      if (this.dialog?.open) this.close(false);
      this.formsStarted = false;
      this.ready = false;
    }
    permission() {
      const privacy = window.Shopify?.customerPrivacy;
      return Boolean(privacy?.preferencesProcessingAllowed?.() && privacy?.marketingAllowed?.());
    }
    privacyChanged() {
      clearInterval(this.timer);
      if (!this.permission()) {
        if (this.dialog.open && this.dataset.designMode !== 'true') this.close(false);
        return;
      }
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (saved && Number.isFinite(saved.firstSeen) && saved.firstSeen <= Date.now()) this.state = saved;
        this.save();
      } catch (_) { /* Blocked storage: keep in-memory state for this page. */ }
      if (this.dataset.eligible !== 'true') return;
      if (due(this.state, Date.now(), 0, this.cooldown)) this.ensureStyles();
      this.timer = setInterval(() => this.maybeOpen(), 1000);
      this.maybeOpen();
    }
    save() {
      if (!this.permission()) return;
      try { localStorage.setItem(KEY, JSON.stringify(this.state)); } catch (_) { /* Private browsing can deny storage. */ }
    }
    blocked() {
      if (document.hidden || /\/(?:checkouts?|cart)(?:\/|$)/.test(location.pathname) || /\/challenge/.test(location.pathname)) return true;
      if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return true;
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"], cart-drawer.active, details[open].menu-opening, #shopify-pc__banner, shopify-forms-modal')].some(element => element !== this.dialog && !this.contains(element) && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    }
    maybeOpen() {
      if (!this.permission() || this.opened || !this.isConnected || this.dataset.eligible !== 'true') return;
      if (due(this.state, Date.now(), this.delay, this.cooldown) && !this.blocked()) this.open(false);
    }
    ensureStyles() {
      if (this.stylesReady || this.dataset.designMode === 'true') return Promise.resolve(true);
      if (this.stylesPromise) return this.stylesPromise;
      this.stylesPromise = new Promise(resolve => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = this.dataset.cssUrl;
        link.addEventListener('load', () => { this.stylesReady = true; resolve(true); }, { once: true });
        link.addEventListener('error', () => { link.remove(); this.stylesPromise = null; resolve(false); }, { once: true });
        document.head.appendChild(link);
      });
      return this.stylesPromise;
    }
    async open(preview) {
      if (this.dialog.open || (!preview && this.blocked())) return;
      if (!await this.ensureStyles()) return;
      if (!this.isConnected || this.dialog.open || (!preview && (!this.permission() || this.blocked()))) return;
      this.preview = preview;
      this.opened = true;
      clearInterval(this.timer);
      this.previousFocus = document.activeElement;
      this.setAttribute('data-fc-open', '');
      this.dialog.show();
      this.dialog.setAttribute('aria-modal', 'true');
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.engine.focus({ preventScroll: true });
    }
    close(dismiss) {
      this.pause();
      if (dismiss && !this.preview) { this.state.closedAt = Date.now(); this.save(); }
      this.dialog.close();
      this.removeAttribute('data-fc-open');
      document.documentElement.style.overflow = this.previousOverflow || '';
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }
    toggleEngine() {
      if (this.won || !this.dialog.open) return;
      const status = this.gameStatus;
      if (this.running) {
        // Validate precisely the last frame displayed. Do not extrapolate a newer
        // angle on click: visual position and the winning predicate share this.rpm.
        this.running = false;
        cancelAnimationFrame(this.raf);
        if (this.rpm >= 4000 && this.rpm <= 5000) {
          this.won = true;
          status.textContent = 'Motor pornit!';
          this.liveStatus.textContent = 'Motor pornit! Ai deblocat formularul de abonare.';
          this.screen('form');
        } else {
          status.textContent = 'Ai ratat zona verde. Mai încearcă!';
          this.engine.textContent = 'START MOTOR';
        }
        return;
      }
      this.paint(0);
      this.startedAt = performance.now();
      this.running = true;
      status.textContent = 'Oprește acul între 4.000 și 5.000 RPM!';
      this.engine.textContent = 'STOP ACUM';
      const tick = now => {
        if (!this.running) return;
        const cycle = (now - this.startedAt) / this.period;
        const phase = cycle % 2;
        this.paint((phase <= 1 ? phase : 2 - phase) * 8000);
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
    pause() {
      cancelAnimationFrame(this.raf);
      if (!this.running) return;
      this.running = false;
      this.engine.textContent = 'START MOTOR';
      this.gameStatus.textContent = 'Joc întrerupt. Apasă START MOTOR pentru o nouă încercare.';
    }
    paint(value) {
      this.rpm = Math.round(Math.min(8000, Math.max(0, value)));
      this.needle.setAttribute('transform', `rotate(${-135 + this.rpm / 8000 * 270} 180 180)`);
      this.rpmDisplay.textContent = this.rpmFormat.format(this.rpm);
    }
    screen(name) {
      if (name === 'form') this.observeForms();
      if (name === 'form') { this.dialog.removeAttribute('aria-labelledby'); this.dialog.setAttribute('aria-label', 'Abonare newsletter FOXSUR'); }
      this.querySelectorAll('[data-fc-screen]').forEach(element => { element.hidden = element.dataset.fcScreen !== name; });
      this.querySelector(`[data-fc-screen="${name}"] h2`)?.focus({ preventScroll: true });
      this.dialog.scrollTop = 0;
    }
    // Forms owns validation, consent, CAPTCHA, submission, and server responses.
    // This code never intercepts or resubmits its form and never infers subscription
    // from localStorage. The browser flag below only suppresses repeated display.
    roots(start = this) {
      const roots = [start];
      for (let i = 0; i < roots.length; i++) {
        roots[i].querySelectorAll('*').forEach(el => { if (el.shadowRoot) roots.push(el.shadowRoot); });
      }
      return roots;
    }
    observeForms() {
      if (this.formsStarted) return;
      this.formsStarted = true;
      const observed = new WeakSet();
      const guarded = new WeakSet();
      const inspect = () => {
        for (const root of this.roots(this.formScreen)) {
          if (!observed.has(root)) { observed.add(root); this.formsObserver.observe(root, { childList: true, subtree: true, characterData: true }); }
          for (const form of root.querySelectorAll('form[data-testid="form"]')) {
            if (guarded.has(form)) continue;
            guarded.add(form);
            const consent = form.querySelector('input[name="emailConsent"]');
            if (consent) consent.required = true;
            form.addEventListener('submit', event => {
              if (!this.won || !consent?.checked) {
                event.preventDefault(); event.stopImmediatePropagation();
                const warning = this.querySelector('[data-fc-consent-warning]');
                warning.hidden = false;
                warning.textContent = 'Bifează acordul pentru comunicări comerciale pentru a revendica reducerea.';
                consent?.focus();
              } else { this.querySelector('[data-fc-consent-warning]').hidden = true; }
            }, { capture: true, signal: this.abort.signal });
          }
          if (this.won && [...root.querySelectorAll('h1,h2,h3,[role="heading"]')].some(el => el.textContent.trim() === 'CERERE PRIMITĂ' && el.getClientRects().length > 0)) {
            if (!this.preview && !this.state.submitted) { this.state.submitted = true; this.save(); }
            if (!this.received) { this.received = true; this.screen('receipt'); clearInterval(this.formsTimer); this.formsObserver.disconnect(); }
          }
        }
      };
      this.formsObserver = new MutationObserver(inspect);
      inspect();
      this.formsTimer = setInterval(() => { if (!this.isConnected) return clearInterval(this.formsTimer); inspect(); }, 2000);
    }
    trapFocus(event) {
      if (event.key !== 'Tab') return;
      // Do not trap focus away from Shopify's own verification controls.
      if (!this.contains(event.target)) return;
      const focusable = this.roots().flatMap(root => [...root.querySelectorAll('button:not(:disabled),input:not(:disabled):not([type="hidden"]),a[href],[tabindex="0"]')]).filter(el => !el.closest('[hidden]') && el.getClientRects().length);
      if (!focusable.length) return;
      let active = document.activeElement;
      while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    }
  }
  customElements.define('fox-challenge', FoxChallenge);
})();
